import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import type { ReactNode } from "react";

// Type is a scale, not a font size. Text is body copy; Heading picks its
// element from `level` and its look from `size`, so outline and design stay
// independent.

const text = cva("", {
  variants: {
    size: {
      xs: "text-xs leading-5",
      sm: "text-sm leading-6",
      md: "text-base leading-7",
      lg: "text-lg leading-8",
      xl: "text-xl leading-8 sm:text-[1.375rem] sm:leading-9",
      /** No size class of its own: reads at its parent's font size, so a word bolded inside a
       * paragraph (a lede's emphasised brand name) never breaks step with the sentence around it. */
      inherit: "",
    },
    tone: {
      default: "text-fg",
      muted: "text-fg-muted",
      subtle: "text-fg-subtle",
      primary: "text-primary",
      success: "text-success",
      inherit: "text-inherit",
    },
    weight: {
      regular: "font-normal",
      medium: "font-medium",
      semibold: "font-semibold",
    },
    align: { start: "text-left", center: "text-center" },
    balance: { true: "text-balance" },
    /** Cut long copy to a number of lines. */
    clamp: { one: "line-clamp-1", two: "line-clamp-2" },
    /** Keep the line breaks of written text: emails, signatures, notes. */
    lines: { kept: "whitespace-pre-line" },
    /** Stop a long line at a comfortable length, whatever the column allows: about 72 characters
     * of Geist at the `md` and `lg` sizes, where one `ch` is about 0.67em. */
    measure: { reading: "max-w-[50ch]" },
  },
  defaultVariants: {
    size: "md",
    tone: "default",
    weight: "regular",
    align: "start",
  },
});

export type TextProps = VariantProps<typeof text> & {
  readonly as?: "p" | "span" | "div" | "small" | "strong" | "li" | "dd" | "dt" | "time";
  readonly children?: ReactNode;
  readonly id?: string;
  /** With `as="time"`: the moment in a form a machine reads. */
  readonly dateTime?: string;
};

export function Text({
  as: Element = "p",
  size,
  tone,
  weight,
  align,
  balance,
  clamp,
  lines,
  measure,
  ...rest
}: TextProps) {
  return (
    <Element
      className={text({
        size,
        tone,
        weight,
        align,
        balance,
        clamp,
        lines,
        measure,
      })}
      {...rest}
    />
  );
}

const heading = cva("font-semibold tracking-tight text-balance", {
  variants: {
    size: {
      display: "text-4xl leading-[1.08] tracking-[-0.035em] sm:text-6xl",
      xl: "text-3xl leading-tight tracking-[-0.025em] sm:text-4xl",
      lg: "text-2xl leading-tight sm:text-3xl",
      md: "text-xl leading-snug",
      sm: "text-base leading-snug",
    },
    tone: { default: "text-fg", inherit: "text-inherit" },
    align: { start: "text-left", center: "text-center" },
    /** `serif` is for one line per page at most: a greeting, a splash. */
    face: {
      sans: "",
      serif: "font-display font-light tracking-[-0.01em]",
      /** The serif at its own light weight: `serif` keeps the base's semibold, which a face with no
       * bold draws as a smeared 400 (D1278). */
      "light-serif": "font-display font-light! tracking-[-0.01em]",
    },
  },
  defaultVariants: {
    size: "lg",
    tone: "default",
    align: "start",
    face: "sans",
  },
});

const ELEMENT = { 1: "h1", 2: "h2", 3: "h3", 4: "h4" } as const;

export type HeadingProps = VariantProps<typeof heading> & {
  readonly level: 1 | 2 | 3 | 4;
  readonly children?: ReactNode;
  readonly id?: string;
};

export function Heading({ level, size, tone, align, face, ...rest }: HeadingProps) {
  const Element = ELEMENT[level];
  return <Element className={heading({ size, tone, align, face })} {...rest} />;
}

/** A short uppercase label above a heading. */
export function Eyebrow({ children }: { readonly children?: ReactNode }) {
  return <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">{children}</p>;
}
